import ConfirmActionPopup from 'components/ConfirmActionPopup';
import { Views } from 'components/costestimation/UpsertCostEstimation';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import ToolbarButton from 'components/layout/ToolbarButton';
import LoaderComponent from 'components/LoaderComponent';
import {
  EntriesConsumerRenderProps,
  ExtendedBaseEntryDto,
  withEntriesContext
} from 'components/segments/entires/EntriesContext';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Grid, Header, Icon, Table } from 'semantic-ui-react';
import styled from 'styled-components';
import { ProcedureType } from 'ts-types/api.enums';
import { currencyFormatter, numberFormatter } from 'utils/fomattingUtils';

interface ScrollableTableProps {
  componentHeight?: string,
  visibleView: Views
}

const ScrollableTableContainer = styled.div<ScrollableTableProps>`
  height: ${(props: ScrollableTableProps) =>
      props.componentHeight && (props.visibleView !== Views.SPECIAL) ? props.componentHeight : "250px"};
  overflow-y: auto;
  width: 100%;
  border: 1px solid rgba(34, 36, 38, .15);
  box-shadow: none;
  border-radius: .28571429rem;

  & .ui.table {
    border: none;
  }

  & .ui.table thead tr:first-child > th {
    position: sticky !important;
    top: 0;
    z-index: 2;
    background: #fff;
  }

  & .ui.table tbody tr:last-child > td {
    border-bottom: 1px solid rgba(34, 36, 38, .15)
  }
`;

const StyledDiv = styled.div`
  padding-top: 1rem;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-bottom: 5px;
  margin-bottom: 0px;
`;

const HeaderTitle = styled.div`
  width: 30%;
  margin-bottom: 0;
`;

const HeaderContent = styled.div`
  width: 20%;
  margin-bottom: 0;
`;

const ToolbarContainer = styled.div`
  width: 50%;
  display: inline;
  flex: 1;
  text-align: right;
`;

interface Props extends MessageSourceProps, EntriesConsumerRenderProps {
  headerText: string,
  tarmedTariff: boolean,
  componentHeight?: string,
  visibleView: Views,
  onUpsertTarmedProcedure: (tarmedProcedure: ExtendedBaseEntryDto) => void,
  onUpsertSpecialProcedure: (specialProcedure?: ExtendedBaseEntryDto) => void
  onEditProcedure: (procedure: ExtendedBaseEntryDto) => void
  onEditMedicament: (medicament: ExtendedBaseEntryDto) => void
}

interface State {
  selectedEntry?: ExtendedBaseEntryDto,
  error: boolean
}

class EntriesSegment extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      selectedEntry: undefined,
      error: false
    };
  }

  markEntry = (entry: ExtendedBaseEntryDto) => {

    const {selectedEntry} = this.state;

    if (!selectedEntry || !this.entriesEqual(selectedEntry, entry)) {
      this.setState({
        selectedEntry: entry
      })
    }
  };

  entriesEqual = (o1: ExtendedBaseEntryDto, o2: ExtendedBaseEntryDto): boolean => {
    if (!o1) {
      return !!o2;
    }

    if (!o2) {
      return false;
    }

    return o1.code === o1.code && o1.medicamentId === o2.medicamentId && o1.procedureId === o2.procedureId;
  }

  minusQuantity = () => {

    const {selectedEntry} = this.state;
    const {minusQuantity} = this.props;

    const removed = minusQuantity(selectedEntry!);

    if (removed) {
      this.setState({
        selectedEntry: undefined
      })
    }
  };

  editEntry = (entry: ExtendedBaseEntryDto) => {

    const {
      editEntry,
      onUpsertTarmedProcedure,
      onEditProcedure,
      onEditMedicament,
      onUpsertSpecialProcedure
    } = this.props;

    const result = editEntry(entry);

    if (result) {
      if (result.tarmedProcedure) {
        onUpsertTarmedProcedure(result);
      } else if (!!result.procedureId) {
        onEditProcedure(result);
      } else if (!!result.medicamentId) {
        onEditMedicament(result);
      } else {
        onUpsertSpecialProcedure(result);
      }
    }
  };

  deleteSelectedEntry = () => {
    const {selectedEntry} = this.state;
    const {deleteEntry} = this.props;

    deleteEntry(selectedEntry!);

    this.setState({
      selectedEntry: undefined
    });
  };

  flatMapEntriesWithBulksAndDescendants = (): Array<ExtendedBaseEntryDto> => {

    const {currentEntries} = this.props;

    const flattenedEntries: Array<ExtendedBaseEntryDto> = [];

    currentEntries.forEach(entry => {

      if (entry.bulks && entry.bulks.length > 0) {

        const mappedBulks: Array<ExtendedBaseEntryDto> = entry.bulks
            .map(bulk => {

              const bulkList = [{
                ...bulk,
                side: bulk.code == entry.code ? entry.side : "",
                tarmedTariff: true
              }];

              if (bulk.bastardProcedure) {
                bulkList.push({
                  ...bulk.bastardProcedure,
                  side: bulk.bastardProcedure.code == entry.code ? entry.side : "",
                  tarmedTariff: true
                })
              }

              return bulkList;
            })
            .flat(1)
            .filter(bulk => bulk.quantity > 0);

        flattenedEntries.push(...mappedBulks);
      } else {
        flattenedEntries.push(entry);

        if (entry.bastardProcedure) {
          flattenedEntries.push(entry.bastardProcedure);
        }
      }

      if (entry.descendants && entry.descendants.length > 0) {
        entry.descendants.forEach(descendant => {
          if (descendant.quantity > 0) {
            flattenedEntries.push(descendant);

            if (descendant.bastardProcedure) {
              flattenedEntries.push(descendant.bastardProcedure);
            }
          }
        })
      }

      if (entry.additionalEfforts && entry.additionalEfforts.length > 0) {
        const additionalEfforts = entry.additionalEfforts
            .sort((ae1, ae2) => ae1.oldType.localeCompare(ae2.oldType))
            .map(additionalEffort => {
              return {
                ...entry,
                ...additionalEffort,
                code: "",
                type: ProcedureType.CHF
              }
            });

        flattenedEntries.push(...additionalEfforts);
      }
    });

    return flattenedEntries;
  };

  getEntryCode = (entry: ExtendedBaseEntryDto): string => {
    // TODO OC: there was parentId here, check why!
    if (entry.procedureId || entry.medicamentId) {
      return entry.code;
    }

    return "";
  };

  render(): React.ReactNode {

    const {selectedEntry} = this.state;
    const {
      headerText,
      priceTotal,
      getMessage,
      tarmedTariff,
      onUpsertSpecialProcedure,
      loadingEmergencyData
    } = this.props;

    const {
      plusQuantity,
      componentHeight,
      visibleView
    } = this.props;

    const flattenedEntries = this.flatMapEntriesWithBulksAndDescendants();

    const mappedEntries = flattenedEntries.map((entry, index) => {
      return (
          <Table.Row
              key={index}
              onClick={() => this.markEntry(entry)}
              active={selectedEntry && this.entriesEqual(selectedEntry, entry)}
              disabled={!!entry.parentUuID || entry.isBastardProcedure}
          >
            <Table.Cell width={1} textAlign="right">
              {numberFormatter.format(entry.quantity)}
            </Table.Cell>
            {tarmedTariff && <Table.Cell width={1} textAlign="center">{entry.side}</Table.Cell>}
            <Table.Cell width={2}>{this.getEntryCode(entry)}</Table.Cell>
            <Table.Cell width={11}>{getMessage(entry.description)}</Table.Cell>
            <Table.Cell width={2} textAlign="right">{entry.value && numberFormatter.format(entry.value)}</Table.Cell>
            <Table.Cell width={1} textAlign="center">{entry.type}</Table.Cell>
          </Table.Row>
      )
    });

    return (
        <StyledDiv>
          <HeaderContainer>
            <HeaderTitle>
              <Header style={{fontSize: "1.1em"}}>
                {headerText}
              </Header>
            </HeaderTitle>
            <HeaderContent>
              <Header style={{fontSize: "1.1em"}}>
                {currencyFormatter.format(priceTotal)}
              </Header>
            </HeaderContent>
            <ToolbarContainer style={{marginBottom: "5px"}}>
              <ToolbarButton
                  customTrigger={this.renderAddSpecialProcedureTrigger()}
                  onClick={() => onUpsertSpecialProcedure()}
                  dataTooltip={getMessage('addSpecialProcedure')}
              />
              <ToolbarButton iconName="plus"
                             iconSize="large"
                             disabled={!selectedEntry || selectedEntry.tarmedProcedure}
                             onClick={() => plusQuantity(selectedEntry!)}
                             dataTooltip={getMessage('plusQuantity')}
              />
              <ToolbarButton iconName="minus"
                             iconSize="large"
                             disabled={!selectedEntry || selectedEntry.tarmedProcedure}
                             onClick={this.minusQuantity}
                             dataTooltip={getMessage('minusQuantity')}
              />
              <ToolbarButton iconName="edit outline"
                             iconSize="large"
                             disabled={!selectedEntry}
                             onClick={() => this.editEntry(selectedEntry!)}
                             dataTooltip={getMessage("editEntry")}
              />
              <ConfirmActionPopup
                  message={getMessage("confirmDeleteEntry")}
                  renderTarget={this.renderDeleteTrigger()}
                  onConfirmAction={this.deleteSelectedEntry}
                  disabled={!selectedEntry}
              />
            </ToolbarContainer>
          </HeaderContainer>
          <PraxFormGrid>
            <Grid.Row>
              <Grid.Column>
                <ScrollableTableContainer
                    componentHeight={componentHeight}
                    visibleView={visibleView}>
                  <Table celled selectable size="small">
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell width={1}>{getMessage('procedure.quantity.short')}</Table.HeaderCell>
                        {tarmedTariff &&
                            <Table.HeaderCell width={1}>{getMessage('procedure.leftRight')}</Table.HeaderCell>}
                        <Table.HeaderCell width={2}>{getMessage('code')}</Table.HeaderCell>
                        <Table.HeaderCell width={11}>{getMessage('description')}</Table.HeaderCell>
                        <Table.HeaderCell width={2}>{getMessage('value')}</Table.HeaderCell>
                        <Table.HeaderCell width={1}>{getMessage('procedure.type')}</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {loadingEmergencyData ? this.renderLoader() : mappedEntries}
                    </Table.Body>
                  </Table>
                </ScrollableTableContainer>
              </Grid.Column>
            </Grid.Row>
          </PraxFormGrid>
        </StyledDiv>
    );
  }

  renderAddSpecialProcedureTrigger = (): React.ReactNode => {
    return (
        <Icon.Group size="large">
          <Icon name="file outline"/>
          <Icon corner="top right" name="add"/>
        </Icon.Group>
    )
  };

  renderDeleteTrigger = (): React.ReactNode => {

    const {selectedEntry} = this.state;
    const {getMessage} = this.props;

    return (
        <ToolbarButton iconName="trash"
                       iconSize="large"
                       disabled={(!selectedEntry)}
                       dataTooltip={getMessage('deleteEntry')}
        />
    );
  };

  renderLoader = () => {

    const {getMessage} = this.props;

    return (
        <Table.Row>
          <Table.Cell>
            <LoaderComponent
                message={`${getMessage("tarmed.emergencyData.loading")}`}
                dimmerProps={{active: true, inverted: false}}
            />
          </Table.Cell>
        </Table.Row>
    )
  }
}

export default withMessages(withEntriesContext(EntriesSegment));
